/**
 * Operations-dashboard grid panel: renders whichever grid the active tab
 * (and, on the alarms tab, the resolved status filter) selects — the
 * descriptor-backed active-alarms or resolved-repair-log EntityGrid, or
 * the maintenance tab's plain client-side DataGridPro (fed from the
 * already loaded dashboard payload, not its own endpoint). Whichever alarms-
 * tab grid is mounted reports its live filtered total up via
 * onAlarmsBadgeCountChange, so the page's alarms tab badge tracks it
 * (mutually exclusive by alarmStatus — only one of the two ever mounted).
 *
 * Contains:
 * - OperationsGridPanel — default export
 */

// eslint-disable-next-line no-restricted-imports -- maintenance tab uses client-side rows from Redux slice (stay hybrid)
import { DataGridPro } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';

import { EntityGrid } from '@sophocles/data-grids';

export default function OperationsGridPanel({
  activeTab,
  alarmStatus,
  customerId,
  navigate,
  alarmFieldOverrides,
  alarmsExtraParams,
  alarmsVisibilityModel,
  setAlarmsColumnVisibility,
  alarmsGridApiRef,
  resolvedFieldOverrides,
  resolvedExtraParams,
  resolvedVisibilityModel,
  setResolvedColumnVisibility,
  resolvedGridApiRef,
  onAlarmsBadgeCountChange,
  maintenanceApiRef,
  filteredMaintenance,
  maintenanceColumns,
  maintenanceColumnVisibility,
  setMaintenanceColumnVisibility,
}) {
  const alarmsColumnVisibility = useMemo(
    () => ({ model: alarmsVisibilityModel, onChange: setAlarmsColumnVisibility }),
    [alarmsVisibilityModel, setAlarmsColumnVisibility],
  );
  const resolvedColumnVisibility = useMemo(
    () => ({ model: resolvedVisibilityModel, onChange: setResolvedColumnVisibility }),
    [resolvedVisibilityModel, setResolvedColumnVisibility],
  );

  return (
    <>
      {activeTab === 'alarms' && alarmStatus !== 'resolved' && (
        <EntityGrid
          basePath="operations-alarms"
          fieldOverrides={alarmFieldOverrides}
          extraParams={alarmsExtraParams}
          options={{
            showSearch: false,
            infiniteScroll: true,
            toolbarVariant: 'flat',
            toolbarContent: null,
            showGridSettings: false,
            columnVisibility: alarmsColumnVisibility,
            gridProps: { density: 'compact', columnHeaderHeight: 38 },
            onGridReady: ({ apiRef }) => {
              alarmsGridApiRef.current = apiRef;
            },
            onTotalRowCountChange: onAlarmsBadgeCountChange,
          }}
        />
      )}
      {activeTab === 'alarms' && alarmStatus === 'resolved' && (
        <EntityGrid
          basePath="operations-resolved"
          fieldOverrides={resolvedFieldOverrides}
          extraParams={resolvedExtraParams}
          options={{
            showSearch: false,
            infiniteScroll: true,
            defaultSort: [{ field: 'resolved_at', sort: 'desc' }],
            toolbarVariant: 'flat',
            toolbarContent: null,
            showGridSettings: false,
            columnVisibility: resolvedColumnVisibility,
            gridProps: { density: 'compact', columnHeaderHeight: 38 },
            onGridReady: ({ apiRef }) => {
              resolvedGridApiRef.current = apiRef;
            },
            onTotalRowCountChange: onAlarmsBadgeCountChange,
          }}
        />
      )}
      {activeTab === 'maintenance' && (
        <DataGridPro
          apiRef={maintenanceApiRef}
          rows={filteredMaintenance}
          columns={maintenanceColumns}
          density="compact"
          getRowHeight={() => 'auto'}
          columnHeaderHeight={38}
          columnVisibilityModel={maintenanceColumnVisibility}
          onColumnVisibilityModelChange={setMaintenanceColumnVisibility}
          disableRowSelectionOnClick
          hideFooter
          initialState={{
            sorting: {
              sortModel: [
                { field: 'status', sort: 'desc' },
                { field: 'date_reported', sort: 'desc' },
              ],
            },
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
            // Maintenance rows are auto-height (Issue/Resolution wrap),
            // so without this cells top-align next to the tallest sibling.
            '& .MuiDataGrid-cell': { alignItems: 'center' },
          }}
          onRowClick={rowParams => {
            const { site_id, site_group_id } = rowParams.row;
            if (site_id && site_group_id) {
              navigate(`/c/${customerId}/plant/${site_group_id}/structure/${site_id}/maintenancehist`);
            }
          }}
        />
      )}
    </>
  );
}
